/* TK collection pages: infinite scroll + keeping the shopper's place on Back.
   Markup: snippets/main-collection-grid.liquid ([data-tk-pagination] page links, [data-tk-more] loader/button).
   Loaded by sections/template--collection.liquid.

   Infinite scroll
   - One loader for the page. When the filters or sort change, the theme redraws the grid; the loader notices,
     cancels anything still loading and starts again from the new grid's next page (no stacked copies, no
     duplicate or skipped pages).
   - Only the grid section is requested (section_id), products already on the page are skipped.
   - A failed request is retried after 1s and 3s, then a "Load more" button appears instead of retrying forever.

   Back button
   - The theme used to save a copy of the whole grid in the browser (megabytes per collection, and it overflowed).
     That is switched off: old copies are cleared on load.
   - On leaving the page we save a small note: which pages were loaded and which product was clicked (or the first
     one on screen) and where it sat on the screen. Coming back, the same pages are fetched again in one go and
     the page scrolls so that product is exactly where it was. When the browser restores the page from its own
     memory (back/forward cache), nothing needs to happen. */
(() => {
  if (window.__tkCollection) return;
  window.__tkCollection = true;

  try {
    Object.keys(sessionStorage).forEach((k) => {
      if (k.indexOf('pagePosition_') === 0 || k.indexOf('pagePagination_') === 0) sessionStorage.removeItem(k);
    });
  } catch (e) {}

  const style = document.createElement('style');
  style.textContent = `
.tk-coll-js .tk-pagination .pagination--default{display:none}
.tk-more{display:flex;justify-content:center;padding:32px 0 8px}
.tk-more__loading{margin:0;font-family:var(--tk-mono,ui-monospace,monospace);font-size:11px;line-height:14px;letter-spacing:.04em;text-transform:uppercase;color:rgba(0,0,0,.55)}
.tk-more__loading span::after{content:'';animation:tk-more-dots 1.2s steps(4) infinite}
@keyframes tk-more-dots{0%{content:''}25%{content:'.'}50%{content:'..'}75%{content:'...'}}
.tk-more .tk-more__btn{min-width:200px;min-height:48px}
.tk-more [hidden]{display:none!important}
`;
  document.head.appendChild(style);
  document.documentElement.classList.add('tk-coll-js');

  const GRID = '.collection-main-body-inner';
  const PAG = '[data-tk-pagination]';
  const MORE = '[data-tk-more]';
  const MAX_RESTORE_PAGES = 8;

  let gen = 0;
  let loading = false;
  let ctrl = null;
  let fails = 0;
  let retryTimer = null;
  let currentGrid = null;
  let observedMore = null;
  let firstPage = 1;
  let lastPage = 1;

  const $ = (s) => document.querySelector(s);
  const sectionId = () => {
    const g = document.getElementById('CollectionProductGrid');
    return g ? g.getAttribute('data-section-id') : '';
  };
  const nextHref = () => {
    const a = document.querySelector(`${PAG} .pagination-next a`);
    const h = a && a.getAttribute('href');
    return h && h !== '#' ? h : null;
  };
  const sectionUrl = (href) => {
    const u = new URL(href, location.href);
    const id = sectionId();
    if (id) u.searchParams.set('section_id', id);
    return u.toString();
  };
  const pageUrl = (href, page) => {
    const u = new URL(href, location.href);
    u.searchParams.set('page', String(page));
    return u.toString();
  };
  const keyOf = (item) => {
    const a = item && item.querySelector && item.querySelector('a[href*="/products/"]');
    if (!a) return null;
    try { return new URL(a.getAttribute('href'), location.href).pathname; } catch (e) { return null; }
  };

  const ui = (state) => {
    const more = $(MORE);
    if (!more) return;
    const l = more.querySelector('[data-tk-more-loading]');
    const b = more.querySelector('[data-tk-more-btn]');
    if (l) l.hidden = state !== 'loading';
    if (b) b.hidden = state !== 'button';
  };

  const append = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const grid = $(GRID);
    if (!grid) return 0;
    const seen = new Set([...grid.children].map(keyOf).filter(Boolean));
    const frag = document.createDocumentFragment();
    let added = 0;
    doc.querySelectorAll(`${GRID} > .grid__item`).forEach((item) => {
      const k = keyOf(item);
      if (k && seen.has(k)) return;
      if (k) seen.add(k);
      frag.appendChild(document.importNode(item, true));
      added++;
    });
    grid.appendChild(frag);
    const oldPag = $(PAG);
    const newPag = doc.querySelector(PAG);
    if (oldPag) {
      if (newPag) oldPag.replaceWith(document.importNode(newPag, true));
      else oldPag.remove();
    }
    const p = newPag ? parseInt(newPag.getAttribute('data-paginate-page'), 10) : NaN;
    if (!isNaN(p)) lastPage = Math.max(lastPage, p);
    if (!nextHref()) ui('none');
    document.dispatchEvent(new CustomEvent('tk:grid:appended', { detail: { added } }));
    return added;
  };

  const sentinelNear = () => {
    const more = $(MORE);
    if (!more) return false;
    return more.getBoundingClientRect().top < window.innerHeight + 1200;
  };

  const loadNext = () => {
    if (loading) return;
    const href = nextHref();
    if (!href) { ui('none'); return; }
    clearTimeout(retryTimer);
    loading = true;
    const my = gen;
    ctrl = new AbortController();
    ui('loading');
    fetch(sectionUrl(href), { signal: ctrl.signal, credentials: 'same-origin' })
      .then((r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then((html) => {
        if (my !== gen) return;
        append(html);
        fails = 0;
      })
      .catch((err) => {
        if (my !== gen || (err && err.name === 'AbortError')) return;
        fails++;
      })
      .finally(() => {
        if (my !== gen) return;
        loading = false;
        if (fails > 0) {
          if (fails <= 2) {
            ui('none');
            retryTimer = setTimeout(() => { if (sentinelNear()) loadNext(); }, fails === 1 ? 1000 : 3000);
          } else {
            ui('button');
          }
          return;
        }
        ui('none');
        // Still near the bottom (tall screens, fast scrolling): keep going.
        if (sentinelNear()) setTimeout(loadNext, 0);
      });
  };

  const io = new IntersectionObserver((entries) => {
    if (entries.some((e) => e.isIntersecting) && fails <= 2) loadNext();
  }, { rootMargin: '0px 0px 1200px 0px' });

  const bind = () => {
    io.disconnect();
    currentGrid = $(GRID);
    const pag = $(PAG);
    const p = pag ? parseInt(pag.getAttribute('data-paginate-page'), 10) : 1;
    firstPage = lastPage = isNaN(p) ? 1 : p;
    const more = $(MORE);
    observedMore = more;
    if (more) io.observe(more);
  };

  // The theme redraws the grid after a filter/sort change: start over from the new grid.
  const reset = () => {
    gen++;
    if (ctrl) ctrl.abort();
    clearTimeout(retryTimer);
    loading = false;
    fails = 0;
    bind();
  };

  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest('[data-tk-more-btn]');
    if (!btn) return;
    event.preventDefault();
    fails = 0;
    loadNext();
  });

  /* ---------- Back button ---------- */
  const KEY = () => 'tkGrid:' + location.pathname + location.search;
  let clicked = null;
  document.addEventListener('click', (event) => {
    const a = event.target.closest && event.target.closest(`${GRID} a[href*="/products/"]`);
    if (!a) return;
    try { clicked = new URL(a.getAttribute('href'), location.href).pathname; } catch (e) {}
  }, true);

  window.addEventListener('pagehide', () => {
    const grid = $(GRID);
    if (!grid) return;
    const items = [...grid.children];
    let el = clicked ? items.find((it) => keyOf(it) === clicked) : null;
    if (!el || el.getBoundingClientRect().bottom < 0 || el.getBoundingClientRect().top > window.innerHeight) {
      el = items.find((it) => it.getBoundingClientRect().bottom > 80);
    }
    const note = {
      first: firstPage,
      last: lastPage,
      anchor: el ? keyOf(el) : null,
      offset: el ? Math.round(el.getBoundingClientRect().top) : 0,
      y: Math.round(window.scrollY),
    };
    try { sessionStorage.setItem(KEY(), JSON.stringify(note)); } catch (e) {}
  });

  const scrollToNote = (note) => {
    const grid = $(GRID);
    const el = note.anchor && grid ? [...grid.children].find((it) => keyOf(it) === note.anchor) : null;
    const y = el ? el.getBoundingClientRect().top + window.scrollY - note.offset : note.y;
    window.scrollTo(0, Math.max(0, y));
  };

  const restore = () => {
    let nav = 'navigate';
    try { nav = performance.getEntriesByType('navigation')[0].type; } catch (e) {}
    if (nav !== 'back_forward') return false;
    let note = null;
    try { note = JSON.parse(sessionStorage.getItem(KEY()) || 'null'); } catch (e) {}
    if (!note || note.first !== firstPage) return false;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    const base = nextHref();
    const upto = Math.min(note.last, firstPage + MAX_RESTORE_PAGES);
    if (!base || upto <= firstPage) {
      // Only the first page was loaded: just put the product back where it was.
      setTimeout(() => scrollToNote(note), 0);
      return true;
    }
    loading = true;
    const my = gen;
    ui('loading');
    const pages = [];
    for (let p = firstPage + 1; p <= upto; p++) pages.push(p);
    Promise.all(pages.map((p) => fetch(sectionUrl(pageUrl(base, p)), { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : null)).catch(() => null)))
      .then((htmls) => {
        if (my !== gen) return;
        for (const html of htmls) { if (!html) break; append(html); }
        scrollToNote(note);
        // Images above can still settle; correct once more on the next frames.
        setTimeout(() => scrollToNote(note), 250);
      })
      .finally(() => {
        if (my !== gen) return;
        loading = false;
        ui('none');
      });
    return true;
  };

  const start = () => {
    if (!$(GRID)) return;
    bind();
    restore();
    // Watch the whole page (the theme may swap the section, the grid or only the loader); the check is two lookups.
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      setTimeout(() => {
        queued = false;
        // A filter/sort redraw replaces the grid and/or the loader element: start over from the new page 1.
        if ($(GRID) && ($(GRID) !== currentGrid || $(MORE) !== observedMore)) reset();
      }, 50);
    }).observe(document.body, { childList: true, subtree: true });
    // Backup for the observer above (it can be paused in background or embedded views): a light scroll check.
    let scrollTimer = null;
    window.addEventListener('scroll', () => {
      if (scrollTimer) return;
      scrollTimer = setTimeout(() => {
        scrollTimer = null;
        if (!loading && fails <= 2 && sentinelNear()) loadNext();
      }, 150);
    }, { passive: true });
  };

  window.addEventListener('pageshow', (event) => {
    // Restored from the browser's back/forward cache: the page is exactly as it was; just make sure the
    // loader is watching again.
    if (!event.persisted) return;
    io.disconnect();
    observedMore = $(MORE);
    if (observedMore) io.observe(observedMore);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
